import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { CheckCircle, ArrowRight, Shield, TrendingUp } from "lucide-react";
import { mockData } from "../mock/mockData";
import ComingSoonModal from "./ComingSoonModal";

const ProductsSection = () => {
  const { products } = mockData;
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState("");

  const handleViewMore = (productName, productUrl) => {
    if (productName === "MiPila" && productUrl) {
      window.open(productUrl, "_blank");
    } else {
      // Abrir modal profesional en lugar de alert
      setSelectedProduct(productName);
      setModalOpen(true);
    }
  };

  const getProductIcon = (productName) => {
    if (productName === "MiPila") {
      return <Shield className="w-10 h-10 text-[#3E97FF]" />;
    }
    if (productName === "Recobra360") {
      return <TrendingUp className="w-10 h-10 text-[#27AE60]" />;
    }
    return <CheckCircle className="w-10 h-10 text-[#FF6B35]" />;
  };

  return (
    <section id="productos" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#252F4A] mb-6">
            Productos <span className="text-[#3E97FF]">Destacados</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#3E97FF] to-[#27AE60] mx-auto mb-6"></div>
          <p className="text-lg text-[#252F4A]/70 max-w-2xl mx-auto">
            Descubre nuestras plataformas digitales avanzadas. Fusionan nuestra experiencia en gestión empresarial con tecnología innovadora, 
            transformando tus procesos críticos y asegurando eficiencia, cumplimiento y confianza en cada operación.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {products.map((product, index) => (
            <Card 
              key={product.id} 
              className="group hover:shadow-2xl transition-all duration-500 border-0 bg-gradient-to-br from-white to-[#F6F6F6]/50 overflow-hidden"
            >
              <CardHeader className="space-y-4 pb-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-gradient-to-br from-[#3E97FF]/10 to-[#27AE60]/10 rounded-xl group-hover:from-[#3E97FF]/20 group-hover:to-[#27AE60]/20 transition-all duration-300">
                      {getProductIcon(product.name)}
                    </div>
                    <div>
                      <CardTitle className="text-2xl text-[#252F4A] group-hover:text-[#3E97FF] transition-colors">
                        {product.name}
                      </CardTitle>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge 
                          variant="secondary" 
                          className="bg-[#27AE60]/10 text-[#27AE60] border-0"
                        >
                          {product.category}
                        </Badge>
                        <Badge 
                          variant="outline" 
                          className="border-[#3E97FF] text-[#3E97FF]"
                        >
                          {product.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                <CardDescription className="text-[#252F4A]/80 text-base leading-relaxed">
                  {product.description}
                </CardDescription>

                {/* Features */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-[#252F4A] text-lg">Características principales:</h4>
                  <ul className="space-y-2">
                    {product.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-[#27AE60] mt-0.5 flex-shrink-0" />
                        <span className="text-[#252F4A]/80">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>

              <CardFooter className="pt-6">
                <Button 
                  onClick={() => handleViewMore(product.name, product.url)}
                  className="w-full bg-[#3E97FF] hover:bg-[#1C3C73] text-white py-3 rounded-lg transition-all duration-300 group-hover:shadow-lg flex items-center justify-center space-x-2"
                >
                  <span>Ver más detalles</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-[#3E97FF]/5 to-[#27AE60]/5 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-[#252F4A] mb-4">
              ¿Interesado en nuestros productos?
            </h3>
            <p className="text-[#252F4A]/70 mb-6 max-w-2xl mx-auto">
              Contacta con nuestro equipo de especialistas para una demostración personalizada 
              y descubre cómo nuestras soluciones pueden transformar tu empresa.
            </p>
            <Button 
              onClick={() => document.getElementById('contacto').scrollIntoView({ behavior: "smooth" })}
              className="bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white px-8 py-3 rounded-lg transition-all duration-300 hover:shadow-lg"
            >
              Solicitar Demostración
            </Button>
          </div>
        </div>
      </div>

      {/* Modal profesional */}
      <ComingSoonModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        productName={selectedProduct}
      />
    </section>
  );
};

export default ProductsSection;